import { logger } from '../logger'
import { type CheckoutSummaryProduct } from '../../components/Pricing/types/checkout.types'
import {
  type BookSize,
  type DesignedCoverId,
  ProductIds,
} from '@mixtiles/web-backend-shared'
import {
  type AiGeneratedCover,
  type Caption,
  type Cover,
  type Page,
  type PagePhoto,
} from '../../components/PhotoBook/PhotoBookProvider/PhotoBook.types'
import {
  isAiGeneratedCover,
  isPhotoCover,
} from '../../components/PhotoBook/PhotoBookProvider/PhotoBook.utils'
import { DEFAULT_PHOTO_RATIO } from './PhotoBook.consts'
import { getBookPhotosUids, isBlankPageSide } from './photoBookUtils'
import { readMobxSnapshot } from '../../utils/mobx/readOutsideReaction'
import { getBookPricingProduct } from './bookPageTier'
import { type RemotePhoto } from '../../components/PhotoBank/PhotoBank.types'
import { isExpiredPhoto } from '../../components/PhotoBank/photoUtils'
import {
  type SerializedCover,
  type SerializedPage,
  type SerializedPhoto,
  type SerializedPhotoBook,
} from './photoBookDTO.types'
import { LEGACY_PHOTO_BOOK_ID } from './photoBookId'
import { type StoredPhotoBook } from './photoBookSaver.types'
import { DefaultTextStyle } from '../../components/PhotoBook/CoverFontSelector/coverFonts'

const WHOLE_PIXEL_PRECISION = 1e-8

// Every photo in a book is resolved against the bank, and the bank holds every
// photo the account ever uploaded — so it is indexed once per book rather than
// scanned once per photo.
function indexPhotosByUid(remotePhotos: RemotePhoto[]) {
  return new Map(remotePhotos.map((photo) => [photo?.uid, photo]))
}

function indexPhotosByUrl(remotePhotos: RemotePhoto[]) {
  return new Map(remotePhotos.map((photo) => [photo.originalUrl, photo]))
}

// Format PhotoBook data for the products which are sent in /checkout-summary
export function serializePhotoBooksToCheckoutProducts({
  bookSize,
  pageCount,
  quantity,
  isPriceTierEnabled,
}: {
  bookSize: BookSize
  pageCount: number
  quantity: number
  isPriceTierEnabled: boolean
}): CheckoutSummaryProduct[] {
  return [
    {
      id: ProductIds.SKU_TILES,
      pricingProduct: getBookPricingProduct({
        bookSize,
        pageCount,
        isPriceTierEnabled,
      }),
      quantity,
    },
  ]
}

function serializePhotoBookPhoto(
  photo: PagePhoto,
  photoData: RemotePhoto
): SerializedPhoto | null {
  // Extract the crop params with aspect ratio 1 and covert them to absolute values
  const { originalUrl, width, height, uploadedAt } = photoData
  const relativeCropParams = photo.cropParams[DEFAULT_PHOTO_RATIO]
  // Stored books can carry an empty cropParams record (the schema doesn't
  // require the default ratio), and a photo the editor can't crop can't be
  // printed — it reads as missing rather than crashing the whole book.
  if (!relativeCropParams) {
    logger.error('Photo book photo has no default-ratio crop', {
      uid: photo.uid,
    })
    return null
  }
  const cropParams = {
    x: getWholePixelCropValue(relativeCropParams.x, width),
    y: getWholePixelCropValue(relativeCropParams.y, height),
    width: getWholePixelCropValue(relativeCropParams.width, width),
    height: getWholePixelCropValue(relativeCropParams.height, height),
    cropType: relativeCropParams.cropType,
  }

  return {
    url: originalUrl,
    width,
    height,
    cropParams,
    dateTaken: photo.dateTaken,
    uploadedAt,
    hasLowResWarn: photo.hasLowResWarn,
    lowQualityApproved: photo.lowQualityApproved,
    fixedIndex: photo.fixedIndex,
    appendedToEnd: photo.appendedToEnd,
    happyData: photo.happyData,
    caption: photo.caption,
  }
}

function getWholePixelCropValue(
  relativeValue: number,
  photoDimension: number
): number {
  const absoluteValue = relativeValue * photoDimension
  const nearestWholePixel = Math.round(absoluteValue)

  return Math.abs(absoluteValue - nearestWholePixel) < WHOLE_PIXEL_PRECISION
    ? nearestWholePixel
    : Math.floor(absoluteValue)
}

function normalizeCaption(caption: Caption): Caption {
  return {
    text: caption.text || '',
    style: caption.style || DefaultTextStyle,
    fontHeight: caption.fontHeight || 0,
  }
}

function serializeCover(
  cover: Cover,
  photosByUid: ReturnType<typeof indexPhotosByUid>
): SerializedCover {
  if (isPhotoCover(cover)) {
    const coverPhoto = cover.photo
    const remotePhoto = photosByUid.get(coverPhoto.uid)
    return {
      type: cover.type,
      caption: normalizeCaption(cover.caption),
      photo: remotePhoto
        ? serializePhotoBookPhoto(coverPhoto, remotePhoto)
        : null,
    }
  }

  if (isAiGeneratedCover(cover)) {
    return {
      type: cover.type,
      caption: normalizeCaption(cover.caption),
      bookCaption: normalizeCaption(cover.bookCaption),
      photo: null,
      aiGeneratedCoverUrl: cover.coverUrl,
      aiGeneratedColors:
        cover.backgroundColor || cover.textColor
          ? {
              backgroundColor: cover.backgroundColor,
              textColor: cover.textColor,
            }
          : undefined,
      aiGeneratedTitle: cover.aiGeneratedTitle,
    }
  }

  return {
    type: cover.type,
    caption: normalizeCaption(cover.caption),
    photo: null,
    designedCoverId: cover.designedCoverId,
  }
}

// The web and backend store the pages differently. In the web, each page is consisted of a front and back side (because
// it's required for the rendering of the visual photo book), but the backend doesn't care about it and only stores the pages
// as one list. Therefore, we need to unite the front and back pages before sending them to the backend. In addition to
// it, we only send the relevant parameters to the backend.
function serializePages(
  pages: Page[],
  photosByUid: ReturnType<typeof indexPhotosByUid>
) {
  const allPages = pages
    .map((page) => [page.front, page.back])
    .flat()
    .filter((page) => !isBlankPageSide(page))
  return allPages.map((page) => {
    return {
      photos: page.photos.map((photo) => {
        const remotePhoto = photosByUid.get(photo.uid)
        return remotePhoto ? serializePhotoBookPhoto(photo, remotePhoto) : null
      }),
    }
  })
}

// Every photo a serialized book prints, cover first. The order layer reads the
// book back in this shape, where each photo carries the single crop it is
// printed with rather than the editor's crop-per-ratio map.
export function getSerializedBookPhotos(
  cover: SerializedCover,
  pages: SerializedPage[]
): SerializedPhoto[] {
  const pagePhotos = pages.flatMap((page) =>
    page.photos.filter((photo) => photo !== null)
  )
  return cover.photo ? [cover.photo, ...pagePhotos] : pagePhotos
}

export function deserializeCover(
  cover: SerializedCover,
  remotePhotos: RemotePhoto[]
): Cover | null {
  if (!cover.photo && !cover.designedCoverId && !cover.aiGeneratedCoverUrl) {
    return null
  }

  const photo = cover.photo
    ? deserializePhotoBookPhoto(cover.photo, indexPhotosByUrl(remotePhotos))
    : null

  if (!photo && !cover.designedCoverId && !cover.aiGeneratedCoverUrl) {
    return null
  }

  const caption = normalizeCaption(cover.caption)

  if (cover.aiGeneratedCoverUrl) {
    return {
      type: 'ai-generated',
      coverUrl: cover.aiGeneratedCoverUrl,
      backgroundColor: cover.aiGeneratedColors?.backgroundColor,
      textColor: cover.aiGeneratedColors?.textColor,
      aiGeneratedTitle: cover.aiGeneratedTitle,
      caption,
      // A book stored before the field has no title to give back, so a photo
      // or designed cover replacing this one starts untitled.
      bookCaption: normalizeCaption(
        cover.bookCaption ?? { text: '', style: DefaultTextStyle }
      ),
    }
  }

  if (cover.designedCoverId) {
    return {
      type: 'designed',
      designedCoverId: cover.designedCoverId as DesignedCoverId,
      caption,
    }
  }

  return photo
    ? {
        type: 'photo',
        photo,
        caption,
      }
    : null
}

function deserializePhotoBookPhoto(
  formattedPhoto: SerializedPhoto,
  photosByUrl: ReturnType<typeof indexPhotosByUrl>
): PagePhoto | null {
  const rightPhoto = photosByUrl.get(formattedPhoto.url)
  if (!rightPhoto) {
    return null
  }

  return {
    uid: rightPhoto.uid,
    dateTaken: formattedPhoto.dateTaken ?? undefined,
    cropParams: {
      [DEFAULT_PHOTO_RATIO]: {
        x: formattedPhoto.cropParams.x / formattedPhoto.width,
        y: formattedPhoto.cropParams.y / formattedPhoto.height,
        width: formattedPhoto.cropParams.width / formattedPhoto.width,
        height: formattedPhoto.cropParams.height / formattedPhoto.height,
        cropType: formattedPhoto.cropParams.cropType,
      },
    },
    hasLowResWarn: formattedPhoto.hasLowResWarn ?? undefined,
    lowQualityApproved: formattedPhoto.lowQualityApproved ?? undefined,
    fixedIndex: formattedPhoto.fixedIndex ?? undefined,
    appendedToEnd: formattedPhoto.appendedToEnd ?? undefined,
    happyData: formattedPhoto.happyData ?? undefined,
    caption: formattedPhoto.caption ?? undefined,
  }
}

/**
 * The account's saved book, read back into the shape the editor and localStorage
 * use. Photos are resolved against the account's photo bank, so a book whose
 * cover photo is no longer there reads as no book at all.
 */
export function deserializeAccountPhotoBook({
  photoBook,
  accountPhotos = [],
  quantity,
}: {
  photoBook: SerializedPhotoBook | null | undefined
  accountPhotos: RemotePhoto[] | undefined
  quantity: number | undefined
}): StoredPhotoBook | null {
  if (!photoBook) {
    return null
  }

  // Account photos come straight from the backend, bypassing the bank's TTL
  // filter. A missing upload time reads as fresh, matching what
  // RemotePhotoSchema stamps on the bank's side.
  const unexpiredAccountPhotos = accountPhotos.filter(
    (photo) => !photo.uploadedAt || !isExpiredPhoto(photo)
  )

  const cover = deserializeCover(photoBook.cover, unexpiredAccountPhotos)
  if (!cover) {
    return null
  }

  const lastEditedAt = photoBook.lastEditedAt ?? 0
  return {
    bookId: photoBook.bookId ?? LEGACY_PHOTO_BOOK_ID,
    lastEditedAt,
    sortKey: photoBook.sortKey ?? lastEditedAt,
    cover,
    pages: deserializePages(photoBook.pages, unexpiredAccountPhotos),
    size: photoBook.bookSize,
    quantity: quantity ?? 1,
    aiGeneratedCovers: photoBook.aiGeneratedCovers ?? [],
  }
}

export function deserializePages(
  formattedPages: { photos: (SerializedPhoto | null)[] }[],
  remotePhotos: RemotePhoto[]
): Page[] {
  const unformattedPages: Page[] = []
  const photosByUrl = indexPhotosByUrl(remotePhotos)

  for (let i = 0; i < formattedPages.length; i += 2) {
    const frontPage = formattedPages[i]
    const backPage = formattedPages[i + 1]

    const unformattedFront = {
      photos: frontPage.photos
        .map((formattedPhoto) =>
          formattedPhoto
            ? deserializePhotoBookPhoto(formattedPhoto, photosByUrl)
            : null
        )
        .filter((photo) => photo !== null) as PagePhoto[],
    }

    const unformattedBack = backPage
      ? {
          photos: backPage.photos
            .map((formattedPhoto) =>
              formattedPhoto
                ? deserializePhotoBookPhoto(formattedPhoto, photosByUrl)
                : null
            )
            .filter((photo) => photo !== null) as PagePhoto[],
        }
      : {
          photos: [],
        }

    unformattedPages.push({
      front: unformattedFront,
      back: unformattedBack,
    })
  }

  return unformattedPages
}

// Format PhotoBook data for the products which are sent in /order
export function serializePhotoBooksMetadata({
  bookId,
  lastEditedAt,
  sortKey,
  bookSize,
  cover,
  pages,
  remotePhotos,
  quantity,
  aiGeneratedCovers,
}: {
  bookId?: string
  lastEditedAt?: number
  sortKey?: number
  bookSize: BookSize
  cover: Cover
  pages: Page[]
  remotePhotos: RemotePhoto[]
  quantity?: number
  aiGeneratedCovers?: AiGeneratedCover[]
}): SerializedPhotoBook {
  const photosByUid = indexPhotosByUid(remotePhotos)

  return {
    bookId,
    lastEditedAt,
    sortKey,
    bookSize,
    cover: serializeCover(cover, photosByUid),
    pages: serializePages(pages, photosByUid),
    quantity,
    aiGeneratedCovers,
  }
}

export function serializeBookWithPhotos(
  book: StoredPhotoBook,
  remotePhotos: RemotePhoto[]
): SerializedPhotoBook {
  const bookPhotoUids = new Set(getBookPhotosUids(book.cover, book.pages))
  const bookRemotePhotos = readMobxSnapshot(() =>
    remotePhotos.filter((photo) => !!photo && bookPhotoUids.has(photo.uid))
  )
  return serializePhotoBooksMetadata({
    bookId: book.bookId,
    lastEditedAt: book.lastEditedAt,
    sortKey: book.sortKey,
    bookSize: book.size,
    cover: book.cover,
    pages: book.pages,
    remotePhotos: bookRemotePhotos,
    quantity: book.quantity,
    aiGeneratedCovers: book.aiGeneratedCovers,
  })
}
